import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';
import { AutoComplete } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { HelpTopicsSearchResult } from './helpTopicsSearchResult';

import './helpTopicSearch.scss';
import { createAlias } from '../../../utils/alias';
import { getDataLayer } from '../../../services/gtmService/utils';

export const HelpTopicsSearch = ({ faqsData, onSelect }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const formContext = useFormContext();
  const [searchResults, setSearchResults] = useState([]);

  const pushSearchEvent = useDebouncedCallback((searchValue) => {
    getDataLayer().push({ event: 'help_centre_search', search_term: searchValue.trim() });
  }, 500);

  const srInstruction = `Use this input to search for a help topic.
        A list of help topics will be created below that can be accessed using the tab key
        and selected using Enter or Space.`;

  const handleSearch = (searchValue) => {
    const trimmedSearchValue = searchValue.trim();
    if (trimmedSearchValue.length >= 3) {
      pushSearchEvent(trimmedSearchValue);
    } else {
      pushSearchEvent.cancel();
    }
    const filteredResults = [];

    Object.entries(faqsData).forEach(([category, faqs]) => {
      const { questions } = faqs;

      questions.forEach((question) => {
        const questionStr = JSON.stringify(question).toLowerCase();
        const searchStr = searchValue.toLowerCase();

        if (questionStr.indexOf(searchStr) !== -1) {
          filteredResults.push({ ...question, category });
        }
      });
    });

    return setSearchResults(filteredResults);
  };

  const handleSelect = (selectedTopic) => {
    onSelect?.(selectedTopic);
    setTimeout(() => {
      formContext.resetField('helpTopicsSearch');
    }, 0);

    const category = findCategoryTitleById(selectedTopic.id, faqsData);

    const categoryAlias = createAlias(category);
    const questionAlias = createAlias(selectedTopic.question);

    if (category) navigate(`${location.pathname}?category=${categoryAlias}&question=${questionAlias}`);
  };

  return (
    <div className="helpTopicsSearch">
      <AutoComplete
        {...{
          name: 'helpTopicsSearch',
          minCharacters: 3,
          label: 'Help topics search',
          hideLabel: true,
          placeholder: 'Search help topics (min. 3 characters)',
          fieldFormContext: formContext,
          defaultValue: '',
          validation: {},
          srInstruction,
          ResultMarkup: ({ result, clickHandler }) =>
            HelpTopicsSearchResult({ result, clickHandler, autoCompleteField: 'helpTopicsSearch' }),
          searchResults,
          onSearch: handleSearch,
          onSelect: handleSelect
        }}
      />
    </div>
  );
};

HelpTopicsSearch.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  faqsData: PropTypes.object,
  onSelect: PropTypes.func
};

const findCategoryTitleById = (id, faqsData) => {
  const match = Object.values(faqsData).find((category) => category.questions.some((q) => q.id === id));

  return match ? match.title : null;
};
